import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SocketProvider } from './context/SocketContext';
import EntryPage from './pages/EntryPage';
import Navbar from './components/shared/Navbar';
import Footer from './components/shared/Footer';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ForgotPassword from "./pages/ForgotPassword";
import ProductDetail from './pages/ProductDetail';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from "./pages/ResetPassword";
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Orders from './pages/Orders';
import SellerDashboard from './pages/SellerDashboard';
import SizeGuide from "./pages/SizeGuide";
import ShippingReturns from "./pages/ShippingReturns";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import './index.css';
import VerifyOtp from "./pages/VerifyOtp";
import ScrollToTop from "./components/ScrollToTop";
import DesignEditor from "./pages/DesignEditor";
import DesignsList from "./pages/DesignsList";
import AffordableProducts from "./pages/AffordableProducts";
import SellerProfile from "./pages/SellerProfile";

const ProtectedRoute = ({ children, sellerOnly }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-lux-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if (sellerOnly && !['seller', 'main_admin', 'local_admin'].includes(user.role)) return <Navigate to="/" />;
  return children;
};

const Layout = ({ children, noFooter }) => (
  <div className="flex flex-col min-h-screen bg-lux-bg transition-all duration-700">
    <Navbar />
    <main className="flex-grow pt-[84px] md:pt-[104px]">
      {children}
    </main>
    {!noFooter && <Footer />}
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<EntryPage />} />
      <Route path="/verify-otp" element={<VerifyOtp />} />
      <Route path="/home" element={<Layout><Home /></Layout>} />
      <Route path="/shop" element={<Layout><Shop /></Layout>} />
      <Route path="/product/:id" element={<Layout><ProductDetail /></Layout>} />
      <Route path="/login" element={<Layout noFooter><Login /></Layout>} />
      <Route path="/register" element={<Layout noFooter><Register /></Layout>} />
      <Route path="/cart" element={<Layout><Cart /></Layout>} />
      <Route path="/forgot-password" element={<Layout noFooter><ForgotPassword /></Layout>} />
      <Route path="/size-guide" element={<Layout><SizeGuide /></Layout>} />
      <Route path="/shipping-returns" element={<Layout><ShippingReturns /></Layout>} />
      <Route path="/faq" element={<Layout><FAQ /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/reset-password/:token" element={<Layout noFooter><ResetPassword /></Layout>} />
      <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />

      {/* Public: Affordable products */}
      <Route path="/affordable" element={<Layout><AffordableProducts /></Layout>} />

      {/* Public: Seller profile */}
      <Route path="/seller/:id" element={<Layout><SellerProfile /></Layout>} />

      <Route path="/wishlist" element={
        <ProtectedRoute><Layout><Wishlist /></Layout></ProtectedRoute>
      } />

      <Route path="/orders" element={
        <ProtectedRoute><Layout><Orders /></Layout></ProtectedRoute>
      } />

      <Route path="/seller/dashboard" element={
        <ProtectedRoute sellerOnly><Layout noFooter><SellerDashboard /></Layout></ProtectedRoute>
      } />

      <Route path="/design-editor" element={
        <ProtectedRoute><DesignEditor /></ProtectedRoute>
      } />

      <Route path="/my-designs" element={
        <ProtectedRoute><Layout><DesignsList /></Layout></ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    // Added Future Flags to silence v7 deprecation warnings
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <ScrollToTop />
      <AuthProvider>
        <SocketProvider>
          <CartProvider>
            <AppRoutes />
          </CartProvider>
        </SocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}